import React from 'react';
import styled from 'styled-components';
import { Typography } from 'antd';

import TagTermGroup from '../../../../../shared/tags/TagTermGroup';
import { SidebarHeader } from './SidebarHeader';
import { useEntityData, useMutationUrn, useRefetch } from '../../../EntityContext';
import { useTranslation } from '../../../../../../i18n/useTranslation';
import { ENTITY_ACTIONS } from '../../../rbacConstantsService';
import { useRbac } from '../../../useRbac';

const SubTypContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
`;

const SubTypeNameText = styled(Typography.Text)`
    font-family: 'Manrope', sans-serif;
    font-weight: 500;
    color: #434343;
    margin-bottom: 8px;
`;

interface Props {
    properties?: any;
    readOnly?: boolean;
}

export const SidebarTagsSection = ({ properties, readOnly }: Props) => {
    const { t } = useTranslation();
    const { entityType, entityData } = useEntityData();

    const { canPerformEntityAction } = useRbac();
    const { TAGS, GLOSSARY_TERMS } = ENTITY_ACTIONS;
    const canManageTags = canPerformEntityAction(entityType, TAGS);
    const canManageGlossaryTerms = canPerformEntityAction(entityType, GLOSSARY_TERMS);
    const noPermissionTooltip = !canManageTags ? t('UNAUTHORISED_TOOLTIP') : undefined;
    const noPermissionTooltipGlossaryTerms = !canManageGlossaryTerms ? t('UNAUTHORISED_TOOLTIP') : undefined;

    const canAddTag = properties?.hasTags;
    const canAddTerm = properties?.hasTerms;

    const mutationUrn = useMutationUrn();

    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = readOnly || entityData?.readOnly?.readOnly === true;
    const isDisabled = isReadOnly || !canManageTags;
    const isDisabledForTerms = isReadOnly || !canManageGlossaryTerms;

    const refetch = useRefetch();

    const subTypeMapper: any = {
        Others: [],
    };

    const terms = entityData?.glossaryTerms?.terms;

    terms?.forEach((info) => {
        const subTypeKey = info?.term?.subTypes?.typeNames?.[0];

        /* eslint-disable @typescript-eslint/dot-notation */
        if (subTypeKey && !subTypeMapper[subTypeKey]) {
            subTypeMapper[subTypeKey] = [info];
        } else if (subTypeKey) {
            subTypeMapper[subTypeKey].push(info);
        } else if (!subTypeMapper['Others']) {
            subTypeMapper['Others'] = [info];
        } else subTypeMapper['Others'].push(info);
    });

    const subTypes = Object.keys(subTypeMapper).reverse();
    const subTypesLength = subTypes.length - 1;

    return (
        <>
            <div className="pa-4">
                <span>
                    <SidebarHeader title={t('Tags')} />
                    <TagTermGroup
                        editableTags={entityData?.globalTags}
                        canAddTag={canAddTag}
                        canRemove={canManageTags}
                        showEmptyMessage
                        entityUrn={mutationUrn}
                        entityType={entityType}
                        refetch={refetch}
                        readOnly={isDisabled}
                        addTagDisabledTooltip={noPermissionTooltip}
                        fontSize={14}
                    />
                </span>
            </div>
            <div className="pa-4">
                <span>
                    <SidebarHeader title={t('Glossary Terms')} />
                    {subTypes.map((type, idx) => (
                        <SubTypContainer>
                            {subTypeMapper[type].length ? (
                                <SubTypeNameText className="f-text-content">{type}</SubTypeNameText>
                            ) : (
                                <></>
                            )}
                            <TagTermGroup
                                isSidebar
                                editableGlossaryTerms={{
                                    ...entityData?.glossaryTerms,
                                    terms: subTypeMapper[type] || [],
                                }}
                                canAddTerm={idx === subTypesLength && canAddTerm}
                                canRemove={canManageGlossaryTerms}
                                showEmptyMessage={!terms?.length}
                                entityUrn={mutationUrn}
                                entityType={entityType}
                                refetch={refetch}
                                readOnly={isDisabledForTerms}
                                addTermDisabledTooltip={noPermissionTooltipGlossaryTerms}
                                fontSize={14}
                            />
                        </SubTypContainer>
                    ))}
                </span>
            </div>
        </>
    );
};
